import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Paper,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { format, isSameDay } from 'date-fns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { CreateBookingSlot } from '../types/bookingSlot';
import { BarberServices } from '../types/barber';
import { roleContext } from '../context/RoleContext';
import { UserRole } from '../types/role';
import {
  getShopSlotsById,
  getBarberSlotsByBarberId,
  getBookingsByShopId,
  getShopSlotsByShopIdAndBarberId,
  getBookingsByBarberId,
} from '../middleware/reservation_service/getRequests';
import {
  getShopBarberById,
  getBarberById,
  getShopOwnerById,
} from '../middleware/registration_service/getRequests';
import { AxiosResponse } from 'axios';
import { Shop } from '../types/shop';
import { Booking } from '../types/booking';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { createBooking } from '../middleware/reservation_service/createRequest';
import { deleteBooking } from '../middleware/reservation_service/deleteRequest';
import Container from '@mui/material/Container';
import ShareCalendarButton from './components/ShareCalendarButton';
import SubscriptionRestricted from './components/SubscriptionRestricted';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';

const ReservationManager: React.FC = () => {
  const partnerItem = localStorage.getItem('reg_partner_id');
  const partnerId = BigInt(partnerItem ? partnerItem : 0);
  const role = useContext(roleContext);
  const shopItem = localStorage.getItem('reg_shop_id');
  const shopId = BigInt(shopItem ? shopItem : 0);
  const { loading: subscriptionLoading, isActive: hasActiveSubscription } =
    useSubscriptionAccess(role);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [bookingSlots, setBookingSlots] = useState<CreateBookingSlot[]>([]);
  const [reservations, setReservations] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<CreateBookingSlot | null>(
    null
  );
  const [barberServices, setBarberServices] = useState<BarberServices[]>([]);
  const [barberData, setBarberData] = useState<any>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
  });

  const [selectedDateReservations, setSelectedDateReservations] = useState<
    Booking[]
  >([]);

  const [slotStartTimes, setSlotStartTimes] = useState<Record<string, string>>(
    {}
  );
  const [slotServiceNames, setSlotServiceNames] = useState<
    Record<string, string>
  >({});

  const [datesWithReservations, setDatesWithReservations] = useState<
    Set<string>
  >(new Set());

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] =
    useState<Booking | null>(null);

  const handleReservationClick = (reservation: Booking) => {
    setSelectedReservation(reservation);
    setIsCancelDialogOpen(true);
  };

  const handleCancelReservation = async () => {
    if (!selectedReservation?.id) return;

    try {
      setLoading(true);
      await deleteBooking(selectedReservation.id);

      setReservations((prev) =>
        prev.filter((res) => res.id !== selectedReservation.id)
      );

      setSelectedDateReservations((prev) =>
        prev.filter((res) => res.id !== selectedReservation.id)
      );

      // Refresh available slots to show the newly freed slot
      await loadSlots();

      setIsCancelDialogOpen(false);
      setSelectedReservation(null);
    } catch (error) {
      console.error('Failed to cancel reservation:', error);
      alert('Failed to cancel reservation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkReservations = async () => {
      try {
        const uniqueDates = new Set<string>();
        let slotResponse: AxiosResponse;

        if (role === UserRole.SHOP_OWNER || role === UserRole.SHOP_BARBER) {
          if (!shopId || shopId === BigInt(0)) return;
          slotResponse = await getShopSlotsById(shopId);
        } else {
          slotResponse = await getBarberSlotsByBarberId(partnerId);
        }

        reservations.forEach((res) => {
          const slot = Array.isArray(slotResponse.data)
            ? slotResponse.data.find((s: any) => s.id === res.slot_id)
            : null;

          if (slot) {
            uniqueDates.add(format(new Date(slot.slot_date), 'yyyy-MM-dd'));
          }
        });

        setDatesWithReservations(uniqueDates);
      } catch (error) {
        console.error('Failed to check reservations for dates:', error);
      }
    };

    if (reservations.length > 0) {
      checkReservations();
    }
  }, [reservations, role, shopId]);

  const hasReservationsForDate = (date: Date): boolean => {
    return datesWithReservations.has(format(date, 'yyyy-MM-dd'));
  };

  const getServiceById = useCallback(
    (serviceId: bigint) => {
      return barberServices.find((s) => BigInt(s.id || 0) === serviceId);
    },
    [barberServices]
  );

  useEffect(() => {
    const fetchSlotTimes = async () => {
      try {
        const times: Record<string, string> = {};
        const serviceNames: Record<string, string> = {};

        let slotResponse: AxiosResponse;
        if (role === UserRole.SHOP_OWNER || role === UserRole.SHOP_BARBER) {
          if (!shopId || shopId === BigInt(0)) return;
          slotResponse = await getShopSlotsById(shopId);
        } else {
          slotResponse = await getBarberSlotsByBarberId(partnerId);
        }

        for (const reservation of selectedDateReservations) {
          if (Array.isArray(slotResponse.data)) {
            const slot = slotResponse.data.find(
              (s: any) => s.id === reservation.slot_id
            );
            if (slot) {
              times[reservation.slot_id] = format(
                new Date(`${slot.slot_date}T${slot.start_time}:00`),
                'HH:mm'
              );

              const service = getServiceById(BigInt(slot.service_id));
              if (service) {
                serviceNames[reservation.slot_id] = service.name as string;
              }
            }
          }
        }

        setSlotStartTimes(times);
        setSlotServiceNames(serviceNames);
      } catch (error) {
        console.error('Failed to fetch slot times:', error);
      }
    };

    if (selectedDateReservations.length > 0) {
      fetchSlotTimes();
    }
  }, [selectedDateReservations, role, shopId]);

  useEffect(() => {
    if (selectedDate) {
      getReservationsForDate(selectedDate)
        .then((reservations) => {
          setSelectedDateReservations(
            reservations.filter((res): res is Booking => res !== null)
          );
        })
        .catch((error) => {
          console.error(
            'Failed to load reservations for selected date:',
            error
          );
        });
    }
  }, [selectedDate, reservations]);

  const [cachedSlots, setCachedSlots] = useState<any[]>([]);

  const loadSlots = useCallback(async () => {
    try {
      setLoading(true);
      let response: AxiosResponse;

      if (role === UserRole.BARBER) {
        response = await getBarberSlotsByBarberId(partnerId);
      } else if (role === UserRole.SHOP_BARBER) {
        response = await getShopSlotsByShopIdAndBarberId(shopId, partnerId);
      } else {
        response = await getShopSlotsById(shopId);
      }

      const slots = Array.isArray(response.data) ? response.data : [];
      setCachedSlots(slots);

      const availableSlots: CreateBookingSlot[] = slots
        .filter((slot: any) => slot.status === 'AVAILABLE')
        .map((slot: any) => ({
          id: slot.id,
          barber_id: BigInt(slot.barber_id),
          service_id: BigInt(slot.service_id),
          start_time: new Date(`${slot.slot_date}T${slot.start_time}:00`),
          status: slot.status,
          slot_date: new Date(slot.slot_date),
          ...(slot.shop_id && { shop_id: BigInt(slot.shop_id) }),
        }));

      setBookingSlots(availableSlots);
    } catch (error) {
      console.error('Failed to load slots:', error);
    } finally {
      setLoading(false);
    }
  }, [role, partnerId, shopId]);

  const loadShopOwnerData = async () => {
    try {
      const response = await getShopOwnerById(partnerId);

      if (
        response.data &&
        response.data.shops &&
        response.data.shops.length > 0
      ) {
        const shop: Shop = response.data.shops[0];

        const allServices: BarberServices[] = [];
        shop.shopBarbers?.forEach((barber) => {
          if (barber.barberShopServices) {
            allServices.push(...barber.barberShopServices);
          }
        });

        setBarberServices(allServices);
        setBarberData(shop.shopBarbers);
      }
    } catch (error) {
      console.error('Failed to load shop owner data:', error);
    }
  };

  const loadBarberData = useCallback(
    async (barberId: bigint) => {
      try {
        let response: AxiosResponse;

        if (role === UserRole.SHOP_OWNER) {
          await loadShopOwnerData();
          return;
        } else if (role === UserRole.SHOP_BARBER) {
          response = await getShopBarberById(barberId);
        } else {
          response = await getBarberById(barberId);
        }

        if (response.data) {
          setBarberData(response.data);

          if (role === UserRole.SHOP_BARBER) {
            if (response.data.barberShopServices) {
              setBarberServices(response.data.barberShopServices);
            }
          } else if (role === UserRole.BARBER) {
            if (response.data.barberServices) {
              setBarberServices(response.data.barberServices);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load barber data:', error);
      }
    },
    [role]
  );

  const loadReservations = useCallback(async () => {
    try {
      setLoading(true);
      let response: AxiosResponse;
      if (role === UserRole.BARBER) {
        response = await getBookingsByBarberId(partnerId);
      } else {
        response = await getBookingsByShopId(shopId);
      }

      const bookings = Array.isArray(response.data) ? response.data : [];

      setReservations(bookings);
    } catch (error) {
      console.error('Failed to load reservations:', error);
    } finally {
      setLoading(false);
    }
  }, [role, shopId]);

  const getBarberNameById = (barberId: bigint) => {
    if (Array.isArray(barberData)) {
      const barber = barberData.find((b) => String(b.id) === String(barberId));
      return barber ? `${barber.firstName} ${barber.lastName}` : null;
    }
    return barberData && String(barberData.id) === String(barberId)
      ? `${barberData.firstName} ${barberData.lastName}`
      : null;
  };

  const getBarberEmailById = (barberId: bigint) => {
    if (Array.isArray(barberData)) {
      const barber = barberData.find((b) => String(b.id) === String(barberId));
      return barber?.email;
    }
    return barberData && String(barberData.id) === String(barberId)
      ? barberData.email
      : undefined;
  };

  const getServiceNameById = (serviceId: bigint) => {
    const service = barberServices.find(
      (s) => String(s.id) === String(serviceId)
    );
    return service?.name || null;
  };

  const getServicePriceById = (serviceId: bigint) => {
    const service = barberServices.find(
      (s) => String(s.id) === String(serviceId)
    );
    return service?.price ?? null;
  };

  // Initial data load, skipped until the partner's subscription access is
  // confirmed so no reservation/slot data is fetched while access is restricted.
  useEffect(() => {
    if (subscriptionLoading || !hasActiveSubscription) return;

    loadSlots();
    loadReservations();
    if (partnerId && partnerId !== BigInt(0)) {
      loadBarberData(partnerId);
    }
  }, [
    loadSlots,
    loadReservations,
    loadBarberData,
    partnerId,
    subscriptionLoading,
    hasActiveSubscription,
  ]);

  const handleDateSelect = (value: Date | null) => {
    if (!value) return;
    setSelectedDate(value);
  };

  const getSlotsForDate = (date: Date) => {
    return bookingSlots.filter((slot) => isSameDay(slot.slot_date, date));
  };

  const getReservationsForDate = useCallback(
    async (date: Date) => {
      const reservationsWithDates = await Promise.all(
        reservations.map(async (res) => {
          try {
            const slot = cachedSlots.find((s: any) => s.id === res.slot_id);
            if (slot) {
              if (
                role === UserRole.SHOP_BARBER &&
                BigInt(slot.barber_id) !== partnerId
              ) {
                return null;
              }

              const slotDate = new Date(slot.slot_date);
              return isSameDay(slotDate, date) ? res : null;
            }
          } catch (error) {
            console.error(`Failed to process slot ${res.slot_id}:`, error);
          }
          return null;
        })
      );

      return reservationsWithDates.filter((res) => res !== null);
    },
    [reservations, role, cachedSlots]
  );

  const hasAvailableSlots = (date: Date) => getSlotsForDate(date).length > 0;

  const handleSlotClick = (slot: CreateBookingSlot) => {
    setSelectedSlot(slot);
    setIsDialogOpen(true);
    setFormData({ customerName: '', customerEmail: '', customerPhone: '' });
    setSuccessMessage('');
  };

  const handleCreateReservation = async () => {
    if (
      !selectedSlot ||
      !formData.customerName ||
      !formData.customerEmail ||
      !formData.customerPhone
    ) {
      return;
    }

    try {
      setLoading(true);
      const barberName = getBarberNameById(selectedSlot.barber_id);
      const barberEmail = getBarberEmailById(selectedSlot.barber_id);
      const serviceName = getServiceNameById(selectedSlot.service_id);
      const price = getServicePriceById(selectedSlot.service_id);

      if (!barberName || !serviceName || price == null) {
        alert(
          'Unable to retrieve complete slot information. Please try again.'
        );
        setLoading(false);
        return;
      }

      const reservationData: Booking = {
        slot_id: selectedSlot.id,
        customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone,
        customer_name: formData.customerName,
        barber_name: barberName,
        barber_email_address: barberEmail,
        service_name: serviceName,
        price: price,
        status: 'PENDING',
        type: role === UserRole.BARBER ? 'BARBER' : 'SHOP',
        barber_id: selectedSlot.barber_id,
        ...(role !== UserRole.BARBER && { shop_id: shopId }),
      };

      const response = await createBooking(reservationData);
      if (response.data) {
        setReservations((prev) => [...prev, response.data]);
        setBookingSlots((prev) =>
          prev.filter((slot) => slot.id !== selectedSlot.id)
        );

        setSuccessMessage(
          'Reservation created successfully! Confirmation emails sent.'
        );

        setTimeout(() => {
          setIsDialogOpen(false);
          setFormData({
            customerName: '',
            customerEmail: '',
            customerPhone: '',
          });
          setSelectedSlot(null);
          setSuccessMessage('');
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to create reservation:', error);
      alert('Failed to create reservation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const DayWithSlots = (props: PickersDayProps<Date>) => {
    const { day, outsideCurrentMonth, onDaySelect } = props;
    const hasSlots = !outsideCurrentMonth && hasAvailableSlots(day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isPastDate = day < today;
    const isFutureDate = day > today;
    const hasReservations = hasReservationsForDate(day);
    const dayWithReservation =
      (isPastDate && hasReservations) || (isFutureDate && hasReservations);

    return (
      <PickersDay
        isFirstVisibleCell={true}
        isLastVisibleCell={true}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        sx={{
          ...(hasSlots && {
            backgroundColor: 'rgba(76, 175, 80, 0.3)',
            fontWeight: 'bold',
            '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.5)' },
          }),
          ...(dayWithReservation && {
            backgroundColor: 'rgba(33, 150, 243, 0.2)',
            fontWeight: 'bold',
            '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.3)' },
            cursor: 'pointer',
          }),
        }}
        onDaySelect={onDaySelect}
      />
    );
  };

  const selectedDateSlots = selectedDate ? getSlotsForDate(selectedDate) : [];

  const getCalendarShareUrl = () => {
    const origin = window.location.origin;

    if (role === UserRole.BARBER) {
      return `${origin}/barber/${partnerId}/calendar`;
    }

    if (role === UserRole.SHOP_OWNER) {
      return `${origin}/shop/${shopId}/calendar?ownerId=${partnerId}`;
    }

    return `${origin}/shop/${shopId}/calendar?barberId=${partnerId}`;
  };

  const getProfileShareUrl = () => {
    const origin = window.location.origin;

    if (role === UserRole.BARBER) {
      return `${origin}/barber/${partnerId}`;
    }

    return `${origin}/shop/${shopId}`;
  };

  if (!subscriptionLoading && !hasActiveSubscription) {
    return <SubscriptionRestricted featureName="Reservation Manager" />;
  }

  return (
    <Container
      sx={{
        marginTop: '75px !important',
        width: '100%',
        padding: '0px 15px !important',
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header Banner */}
        <Box
          sx={{
            mb: 3,
            p: 3,
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            borderRadius: 2,
            borderLeft: '5px solid #dd8709',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontFamily: 'GFS Didot' }}>
              Reservation Manager
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Create and manage customer reservations
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <ShareCalendarButton url={getCalendarShareUrl()} />
            <ShareCalendarButton
              url={getProfileShareUrl()}
              label="Share Public Profile"
              dialogTitle="Share Public Profile"
              text="Profile URL"
            />
          </Box>
        </Box>

        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Calendar Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 1, sm: 2 } }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar
                  value={selectedDate}
                  onChange={handleDateSelect}
                  slots={{ day: DayWithSlots }}
                  disabled={loading}
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    mx: 'auto',
                    '& .MuiPickersCalendarHeader-root': {
                      pl: 0.5,
                      pr: 0,
                    },
                    '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer':
                      {
                        justifyContent: 'space-around',
                      },
                    '& .MuiPickersDay-root': {
                      width: { xs: 32, sm: 36 },
                      height: { xs: 32, sm: 36 },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    },
                  }}
                />
              </LocalizationProvider>
            </Paper>
          </Grid>

          {/* Available Slots Panel */}
          <Grid item xs={12} md={6}>
            {selectedDate &&
              (() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                return (
                  <Paper sx={{ p: 2, maxHeight: 400, overflowY: 'auto' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Available Slots - {format(selectedDate, 'MMMM d, yyyy')}
                    </Typography>

                    {loading ? (
                      <Box
                        sx={{ display: 'flex', justifyContent: 'center', p: 3 }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : selectedDateSlots.length === 0 ? (
                      <Typography color="text.secondary">
                        No available slots for this date
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        {selectedDateSlots.map((slot) => {
                          const service = getServiceById(slot.service_id);
                          return (
                            <Card
                              key={slot.id}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { boxShadow: 3 },
                                transition: 'box-shadow 0.3s',
                              }}
                              onClick={() => handleSlotClick(slot)}
                            >
                              <CardContent>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1,
                                  }}
                                >
                                  <PersonIcon fontSize="small" />
                                  <Typography variant="body2">
                                    {getBarberNameById(slot.barber_id)}
                                  </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                  {service?.name || 'Service'}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <Chip
                                    icon={<AttachMoneyIcon />}
                                    label={`£${service?.price || '0'}`}
                                    size="small"
                                  />
                                  <Chip
                                    icon={<AccessTimeIcon />}
                                    label={`${service?.duration || 0} min`}
                                    size="small"
                                  />
                                  <Chip
                                    label={format(slot.start_time, 'HH:mm')}
                                    size="small"
                                    color="primary"
                                  />
                                </Box>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    )}
                  </Paper>
                );
              })()}
          </Grid>

          {/* Reservations Panel */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Reservations{' '}
                {selectedDate && `- ${format(selectedDate, 'MMMM d, yyyy')}`}
              </Typography>
              {selectedDateReservations.length === 0 ? (
                <Typography color="text.secondary">
                  No reservations for this date
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {selectedDateReservations.map(
                    (reservation: Booking, index: number) => (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={4}
                        key={`${reservation.slot_id}-${index}`}
                      >
                        <Card
                          sx={{
                            position: 'relative',
                            '&:hover .cancel-button': {
                              opacity: 1,
                            },
                            transition: 'all 0.3s',
                          }}
                        >
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              {reservation.customer_name}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 0.5,
                              }}
                            >
                              <EmailIcon fontSize="small" color="action" />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {reservation.customer_email}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 1,
                              }}
                            >
                              <PhoneIcon fontSize="small" color="action" />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {reservation.customer_phone}
                              </Typography>
                            </Box>
                            {slotServiceNames[reservation.slot_id] && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mb: 1,
                                }}
                              >
                                <ContentCutIcon
                                  fontSize="small"
                                  color="action"
                                />
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                  gutterBottom
                                >
                                  {slotServiceNames[reservation.slot_id]}
                                </Typography>
                              </Box>
                            )}
                            {slotStartTimes[reservation.slot_id] && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mt: 1,
                                }}
                              >
                                <AccessTimeIcon
                                  fontSize="small"
                                  color="action"
                                />
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  {slotStartTimes[reservation.slot_id]}
                                </Typography>
                              </Box>
                            )}
                            <Chip
                              label={reservation.status}
                              size="small"
                              color={
                                reservation.status === 'PENDING'
                                  ? 'info'
                                  : reservation.status === 'CANCELLED'
                                  ? 'warning'
                                  : 'success'
                              }
                              sx={{ mt: 1 }}
                            />
                          </CardContent>

                          <Box
                            className="cancel-button"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'rgba(0, 0, 0, 0.7)',
                              opacity: 0,
                              transition: 'opacity 0.3s',
                              borderRadius: 1,
                            }}
                          >
                            <Button
                              variant="contained"
                              color="error"
                              onClick={() =>
                                handleReservationClick(reservation)
                              }
                            >
                              Cancel Reservation
                            </Button>
                          </Box>
                        </Card>
                      </Grid>
                    )
                  )}
                </Grid>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Create Reservation Dialog */}
        <Dialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create Reservation</DialogTitle>
          <DialogContent>
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}

            {selectedSlot && (
              <>
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Selected Slot
                  </Typography>
                  <Typography variant="body1">
                    {getServiceById(selectedSlot.service_id)?.name} -{' '}
                    {format(selectedSlot.start_time, 'HH:mm')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {format(selectedSlot.slot_date, 'MMMM d, yyyy')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Customer Name"
                    value={formData.customerName}
                    onChange={(e) =>
                      setFormData({ ...formData, customerName: e.target.value })
                    }
                    fullWidth
                    required
                    disabled={loading || !!successMessage}
                  />
                  <TextField
                    label="Customer Email"
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customerEmail: e.target.value,
                      })
                    }
                    fullWidth
                    required
                    disabled={loading || !!successMessage}
                  />
                  <TextField
                    label="Customer Phone"
                    value={formData.customerPhone}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customerPhone: e.target.value,
                      })
                    }
                    fullWidth
                    required
                    disabled={loading || !!successMessage}
                    placeholder="07980123456"
                  />
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateReservation}
              variant="contained"
              disabled={
                loading ||
                !formData.customerName ||
                !formData.customerEmail ||
                !formData.customerPhone ||
                !!successMessage
              }
            >
              {loading ? 'Creating...' : 'Create Reservation'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Cancel Reservation Dialog */}
        <Dialog
          open={isCancelDialogOpen}
          onClose={() => setIsCancelDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Cancel Reservation</DialogTitle>
          <DialogContent>
            {selectedReservation && (
              <Box>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to cancel this reservation?
                </Typography>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="subtitle2">
                    Customer: {selectedReservation.customer_name}
                  </Typography>
                  <Typography variant="body2">
                    Email: {selectedReservation.customer_email}
                  </Typography>
                  <Typography variant="body2">
                    Phone: {selectedReservation.customer_phone}
                  </Typography>
                  {slotServiceNames[selectedReservation.slot_id] && (
                    <Typography variant="body2">
                      Service: {slotServiceNames[selectedReservation.slot_id]}
                    </Typography>
                  )}
                  {slotStartTimes[selectedReservation.slot_id] && (
                    <Typography variant="body2">
                      Time: {slotStartTimes[selectedReservation.slot_id]}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsCancelDialogOpen(false)}
              disabled={loading}
            >
              Keep Reservation
            </Button>
            <Button
              onClick={handleCancelReservation}
              variant="contained"
              color="error"
              disabled={loading}
            >
              {loading ? 'Cancelling...' : 'Cancel Reservation'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ReservationManager;
